import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useReport } from '../../../hooks';
import { useAuth } from '../../../context/AuthContext';
import { MermaidDiagram } from './MermaidDiagram';
import { BusinessIntelligenceSection } from './BusinessIntelligenceSection';
import { ROISection } from './ROISection';
import { RoadmapSection } from './RoadmapSection';
import { ReportCharts } from './ReportCharts';
import styles from './ReportViewer.module.css';
import { getApiUrl } from '../../../config/environments';

export const ReportViewer: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { report, isLoading, error } = useReport(reportId || null);
  const { getToken } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (!reportId) return;

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${getApiUrl()}/reports/${reportId}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename =
        `${(report?.title || 'process-report')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'process-report'}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err?.message || 'Unable to download PDF right now.';
      setDownloadError(message);
      console.error('PDF download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  console.log('🔍 ReportViewer render:', {
    reportId,
    isLoading,
    hasError: !!error,
    errorMessage: error?.message,
    hasReport: !!report,
    reportTitle: report?.title
  });

  // Debug: verificar datos para gráficos
  console.log('📊 Chart data check:', {
    hasRoiCalculation: !!report?.roiCalculation,
    hasImplementationRoadmap: !!report?.implementationRoadmap,
    hasBusinessIntelligence: !!report?.businessIntelligence,
    roiCalculation: report?.roiCalculation,
    implementationRoadmap: report?.implementationRoadmap,
    businessIntelligence: report?.businessIntelligence
  });

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>⏳</div>
            <p>Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#d32f2f' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>❌</div>
            <h2>Error Loading Report</h2>
            <p>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Report Not Found</h2>
            <p>The requested report could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ minHeight: '100vh', padding: '2rem' }}>
        <div className={styles.card} style={{ 
          background: '#ffffff',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '1000px',
          width: '100%',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
        {/* Header */}
        <div className={styles.header} style={{ marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #e0e0e0' }}>
          <div>
            <h1 className={styles.title} style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a1a1a', textAlign: 'left', marginBottom: '0.5rem' }}>
              {report.title || 'Process Optimization Report'}
            </h1>
            <p className={styles.subtitle} style={{ color: '#666', textAlign: 'left', marginBottom: '1rem' }}>
              Generated on {report.generatedAt ? new Date(report.generatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : 'Unknown date'}
            </p>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.downloadButton}
              onClick={handleDownloadPdf}
              disabled={isDownloading || !reportId}
            >
              {isDownloading ? 'Generating PDF…' : 'Download PDF'}
            </button>
            {downloadError && (
              <p className={styles.downloadError}>
                {downloadError}
              </p>
            )}
          </div>
        </div>

        {/* Executive Summary */}
        <section className={styles.section} style={{ marginBottom: '2rem' }}>
          <h2 className={styles.sectionTitle} style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1a1a1a', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e0e0e0' }}>
            Executive Summary
          </h2>
          <div className={styles.executiveSummary} style={{ 
            background: '#f5f5f5', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            border: '1px solid #e0e0e0',
            color: '#333',
            lineHeight: '1.8',
            fontSize: '1.05rem'
          }}>
            <ReactMarkdown>{report.executiveSummary || 'No executive summary available.'}</ReactMarkdown>
          </div>
        </section>

        {/* Key Findings */}
        {report.keyFindings && report.keyFindings.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Key Findings</h2>
            <div className={styles.findingsList}>
              {report.keyFindings.map((finding, index) => (
                <div key={index} className={styles.findingItem}>
                  <ReactMarkdown>{finding}</ReactMarkdown>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Business Intelligence */}
        {report.businessIntelligence && (
          <section className={styles.section} style={{ marginBottom: '2rem' }}>
            <BusinessIntelligenceSection data={report.businessIntelligence} />
          </section>
        )}

        {/* Process Diagrams */}
        {report.diagrams && report.diagrams.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Process Flow Diagrams</h2>
            <div className={styles.diagramsContainer}>
              {report.diagrams
                .sort((a, b) => a.order - b.order)
                .map((diagram, index) => (
                  <MermaidDiagram key={index} diagram={diagram} />
                ))}
            </div>
          </section>
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Priority Recommendations</h2>
            <div className={styles.recommendationsList}>
              {report.recommendations.map((recommendation, index) => (
                <div key={index} className={styles.recommendationItem}>
                  <ReactMarkdown>{recommendation}</ReactMarkdown>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ROI Calculation */}
        {report.roiCalculation && (
          <section className={styles.section} style={{ marginBottom: '2rem' }}>
            <ROISection data={report.roiCalculation} />
          </section>
        )}

        {/* Data Visualization Charts */}
        <ReportCharts
          roiCalculation={report.roiCalculation}
          implementationRoadmap={report.implementationRoadmap}
          businessIntelligence={report.businessIntelligence}
        />

        {/* Implementation Roadmap */}
        {report.implementationRoadmap && (
          <section className={styles.section} style={{ marginBottom: '2rem' }}>
            <RoadmapSection data={report.implementationRoadmap} />
          </section>
        )}

        {/* Detailed Sections */}
        {report.sections && report.sections.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Detailed Analysis</h2>
            {report.sections
              .sort((a, b) => a.order - b.order)
              .map((section, index) => (
                <div key={index} className={styles.detailSection}>
                  <h3 className={styles.detailSectionTitle}>{section.title}</h3>
                  <div className={styles.detailSectionContent}>
                    <ReactMarkdown>{section.content || ''}</ReactMarkdown>
                  </div>
                </div>
              ))}
          </section>
        )}
      </div>
    </div>
  );
};
